// Where the session token lives, and why it lives there.
//
// The API authenticates a bearer token it verifies itself: signature, issuer,
// audience, expiry, not-before and a maximum lifetime (apps/api/session.ts).
// The token comes from POST /v1/auth/sign-in (components/SignIn.tsx) — the API
// issues it, this app never mints one — and even then it is not trusted until
// GET /me has accepted it. A screen that "logged you in" without the server
// agreeing would be theatre.
//
// Storage: sessionStorage, not localStorage.
//
//   * It is cleared when the tab closes, which is the closest thing to a
//     session this app can offer before cookie sessions exist.
//   * It is not shared with other tabs, so one workspace per tab is the honest
//     model while the tenant context lives in memory.
//
// This is the user's OWN credential. No provider secret, no connection
// secret_ref and no service-role key is ever sent to this app — the API has no
// endpoint that returns one, and its response schemas would not serialise one
// if a handler tried.
//
// Signing out is now two things rather than one. This file does the local half
// — discard the token — and state/workspace.tsx does the half that matters,
// which is telling the server to stop accepting it (POST /v1/session/revoke,
// migration 0022). Before that endpoint existed, clearing storage was a gesture:
// the token stayed valid until it expired and any copy of it still worked.
// The maximum lifetime the API enforces is still the backstop for a session
// nobody thought to end.

const STORAGE_KEY = 'bk.session';

/**
 * The CSRF cookie's name, mirrored from apps/api/cookies.ts.
 *
 * The API also accepts a session from a `__Host-bk_session` cookie, and nothing
 * in this build mints one: an identity provider does, or whatever terminates
 * TLS in front of the API. This app therefore never READS the session cookie —
 * it cannot, `__Host-` cookies set by an edge are typically HttpOnly and must
 * be — and only reads the CSRF token, which is deliberately not HttpOnly so
 * that the double submit is possible at all.
 *
 * Absent is the ordinary case today. Then nothing is echoed, the API sees a
 * bearer request, and the CSRF rule has nothing to say about it.
 */
const CSRF_COOKIE = '__Host-bk_csrf';

/**
 * The CSRF token to echo, if this page has one.
 *
 * Duplicated names yield nothing, exactly as the API's parser decides: if two
 * cookies claim this name, we do not know which the API will refuse to accept,
 * and guessing would produce a request that fails for a reason nobody can see.
 */
export function csrfToken(): string | null {
  let found: string | null = null;
  try {
    for (const part of document.cookie.split(';')) {
      const separator = part.indexOf('=');
      if (separator < 1) continue;
      if (part.slice(0, separator).trim() !== CSRF_COOKIE) continue;
      if (found !== null) return null;
      found = part.slice(separator + 1).trim();
    }
  } catch {
    return null;
  }
  return found === '' ? null : found;
}

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    // Private mode, or storage disabled by policy. The app still works for the
    // life of the page; it just will not survive a refresh.
    return null;
  }
}

export function loadToken(): string | null {
  try {
    const value = storage()?.getItem(STORAGE_KEY) ?? null;
    return value === '' ? null : value;
  } catch {
    return null;
  }
}

export function saveToken(token: string): void {
  try {
    storage()?.setItem(STORAGE_KEY, token);
  } catch {
    // Not fatal: the token stays in memory for this page.
  }
}

export function clearToken(): void {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do; the in-memory copy is dropped by the caller either way.
  }
}
